import * as React from 'react';
import { Button, Modal } from 'antd';
import autobind from 'autobind-decorator';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { AutocompleteView } from 'src/core/ui/autocomplete/autocomplete.view';
import { ClientSalutationEditorController } from 'src/app/crm/clients/parts/client-salutation-editor.controller';
import { ClientItemEditorController } from 'src/app/crm/clients/parts/client-item-editor.controller';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { DateView } from 'src/core/ui/date.view';
import { TextView } from 'src/core/ui/text.view';
import { TooltipView } from 'src/core/ui/tooltip.view';
import { GenderEditorView } from 'src/app/shared/parts/gender-editor.view';
import { PaxClientFormController } from './pax-client-form.controller';
import { PaxTravelDocument } from './pax-travel-document';
import { NoteView } from '../shared/note.view';

interface QuotePaxViewProps extends ComponentViewProps<DataSet<QuotePaxItem>> {
	identityService: any;
	mode: ControlMode;
	quote: QuoteItem;
	newQuotePax: (quote: QuoteItem, client?: ClientItem) => any;
	billing: BillingItem[];
	formatService: any;
	attachClient: (pax: QuotePaxItem, client: ClientItem) => void;
	deletePaxs: (paxsDelete: QuotePaxItem[]) => Promise<boolean>;
	handleEditQuotePaxField: (quotePax: QuotePaxItem, requireUpdatePrice: boolean, requirePolicyChecking: boolean) => Promise<void>;
	nationalityQuery: DropdownRequest<AutocompleteSummary>;
	documentTypeQuery: DropdownRequest<AutocompleteSummary>;
	defaultLanguage: string;
}
interface PaxState {
	showClientForm: boolean;
	pax: QuotePaxItem;
}
@observer
export class QuotePaxView extends React.Component<QuotePaxViewProps, PaxState> {
	@observable
	client: ClientItem;

	paxColumns: Column[] = [
		{
			key: 'attach',
			title: '',
			dataIndex: 'attach',
			render: (text, record: QuotePaxItem, index) => {
				return <Button.Group>
					<Button
						icon='up'
						onClick={() => this.updateOrder(record, record.order - 1)}
						disabled={this.props.mode == 'display' || record.order == 1}
					/>
					<Button
						icon='down'
						onClick={() => this.updateOrder(record, record.order + 1)}
						disabled={this.props.mode == 'display' || this.props.model.items.length == record.order}
					/>
					<Button icon="paper-clip"
						onClick={() => {
							this.props.attachClient(record, this.client);
							this.forceUpdate();
						}}
						disabled={this.props.mode == 'display' || this.client == null}>
						Attach
					</Button>
					<NoteView
						identityService={this.props.identityService}
						mode={this.props.mode}
						noteProperty="notes"
						record={record.dynamicProperties}
						setBusy={this.props.setBusy}
					/>
					<PaxTravelDocument
						title={`${record.dynamicProperties.surName}, ${record.dynamicProperties.foreName}`}
						mode={this.props.mode}
						travelDocument={record.dynamicProperties as any}
						nationalityQuery={this.props.nationalityQuery}
						documentTypeQuery={this.props.documentTypeQuery}
						setBusy={this.props.setBusy}
					/>
					<Button icon="user"
						onClick={() => this.convert(record)}
						disabled={this.props.mode == 'display'}>
						{record.clientId == null ? "Convert" : "Details"}
					</Button>
				</Button.Group>
			}
		},
		{
			key: 'Salutation',
			dataIndex: 'salutation',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return <ClientSalutationEditorController setBusy={this.props.setBusy}
						label={null}
						mode={this.props.mode}
						value={record.dynamicProperties.salutation != null ? record.dynamicProperties.salutation : null}
						onChanged={newVal => record.dynamicProperties.salutation = newVal}
					/>;
				}
				return <span>{record.dynamicProperties['salutation']}</span>;
			},
			title: 'Salutation'
		},
		{
			key: 'ForeName',
			dataIndex: 'foreName',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<TextView
							required
							mode="edit"
							label={null}
							value={record.dynamicProperties['foreName']}
							onChanged={(newVal) => { record.dynamicProperties['foreName'] = newVal; }}
						/>
					);
				}
				return <span>{record.dynamicProperties['foreName']}</span>;
			},
			title: 'Forename'
		},
		{
			key: 'MiddleName',
			dataIndex: 'middleName',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return <TextView mode='edit' label={null}
						value={record.dynamicProperties['middleName']}
						onChanged={newVal => {
							record.dynamicProperties['middleName'] = newVal;
						}}
					/>;
				}
				return <span>{record.dynamicProperties['middleName']}</span>;
			},
			title: 'Middle Name'
		},
		{
			key: 'SurName',
			dataIndex: 'surName',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<TextView
							required
							mode="edit"
							label={null}
							value={record.dynamicProperties['surName']}
							onChanged={(newVal) => { record.dynamicProperties['surName'] = newVal; }}
						/>
					);
				}
				return <span>{record.dynamicProperties['surName']}</span>;
			},
			title: 'SurName'
		},
		{
			key: 'nationality',
			dataIndex: 'dynamicProperties.nationality',
			title: 'Nationality'
		},
		{
			key: 'BirthDate',
			dataIndex: 'birthDate',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<DateView
							required
							mode="edit"
							label={null}
							value={record.dynamicProperties['birthDate']}
							onChanged={(newVal) => {
								record.dynamicProperties['birthDate'] = newVal;
								this.props.handleEditQuotePaxField(record, true, true);
							}}
							format="small-date"
						/>
					);
				}
				return <span>{this.props.formatService.toString('short-date', record.dynamicProperties['birthDate'])}</span>;
			},
			title: 'BirthDate'
		},
		{
			key: 'Gender',
			dataIndex: 'Gender',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<GenderEditorView
							required
							label={null}
							mode="edit"
							value={record.dynamicProperties['gender']}
							onChanged={(newVal) => { record.dynamicProperties['gender'] = newVal; }}
						/>
					);
				}
				return <span>{record.dynamicProperties['gender']}</span>;
			},
			title: 'Gender'
		},
		{
			key: 'Billing To',
			dataIndex: 'billingTo',
			editable: (text: any, record: QuotePaxItem, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<DropdownView
							required
							label={null}
							model={record.dynamicProperties['billingTo']}
							onChanged={(newVal) => {
								record.dynamicProperties['billingTo'] = newVal.id;
							}}
							keyField="id"
							textField="displayName"
							options={this.props.billing.slice()}
						/>
					);
				}
				const billTo = record.dynamicProperties['billingTo'] && this.props.billing.find(e => e.id == record.dynamicProperties['billingTo']) != null
					? this.props.billing.find(e => e.id == record.dynamicProperties['billingTo']).displayName
					: '';
				return <React.Fragment>{billTo}</React.Fragment>;

			},
			title: 'Billing To'
		}
	];

	constructor(props) {
		super(props);
		this.state = { showClientForm: false, pax: null };
	}

	@autobind
	private updateOrder(item: QuotePaxItem, newOrder: number) {
		const oldOrder = item.order;
		let startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
		const toUpdates = this.props.model.items.slice();
		while (startIndex <= Math.max(newOrder, oldOrder)) {

			if (toUpdates.length < newOrder) {
				newOrder = toUpdates.length;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}
			if (newOrder < 1) {
				newOrder = 1;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}

			const toUpdate = toUpdates.find(e => startIndex == e.order);
			if (toUpdate == null) break;

			const order = newOrder < oldOrder ? startIndex++ + 1 : startIndex++ - 1;
			toUpdate.order = order;
			if (startIndex == oldOrder) {
				break;
			}
		}
		item.order = newOrder;
		this.props.model.items = this.props.model.items.sort((x, y) => x.order - y.order);
	}

	@autobind
	convert(quotePax: QuotePaxItem) {
		this.setState({
			showClientForm: true,
			pax: quotePax
		})
	}

	@autobind
	private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		if (ev != null) {
			ev.preventDefault();
		}
		this.setState({
			showClientForm: false,
			pax: null
		})
	}
	renderClientModal() {
		const { pax, showClientForm } = this.state;
		if (pax == null)
			return null;
		return <PaxClientFormController
			billing={this.props.billing}
			model={pax}
			closeDialog={this.closeDialog}
			type="Person"
			setBusy={this.props.setBusy}
			formatService={this.props.formatService}
			showModal={showClientForm}
			defaultLanguage={this.props.defaultLanguage}
		/>
	}
	render() {
		if (this.props.model == null) {
			return null;
		}

		return (
			<div>
				{this.renderClientModal()}
				<DataTableView
					columns={this.paxColumns}
					mode={this.props.mode}
					setBusy={this.props.setBusy}
					model={this.props.model}
					createNewItem={() => this.props.newQuotePax(this.props.quote, this.client)}
					afterCreateNewItem={(e) => this.client = null}
					afterDeleteItem={this.props.deletePaxs}
					hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination}
					toolbarLeftItems={<div className='ml-3 toolbar-select'>
						<ClientItemEditorController
							label={null}
							setBusy={this.props.setBusy}
							mode={this.props.mode}
							value={this.client != null ? this.client.id : null}
							onChanged={newVal => {
								this.client = newVal;
							}}
							type={0}
						/>
					</div>}
				/>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-paxs/quote-pax.view.tsx